import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Copy, ExternalLink, Mail, MessageSquare } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui';
import { formatPhone } from '@/lib/utils';
import { listSurveys } from '@/services/surveys.service';
import { getPatient, isEmailOptedOut, isSmsOptedOut } from '@/services/patients.service';
import { getErrorMessage } from '@/services/apiClient';
import { sendSMS, sendEmail } from '@/services/communications.service';
import {
  getEFormLink,
  getEmailTemplate,
  getSmsTemplate,
  replaceKeywords,
  type EFormLinkResponse,
} from '@/services/eforms.service';

interface GiFormSendModalProps {
  patientId: number;
  surveyId: number;
  open: boolean;
  onClose: () => void;
}

export function GiFormSendModal({ patientId, surveyId, open, onClose }: GiFormSendModalProps) {
  const [linkData, setLinkData] = useState<EFormLinkResponse | null>(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [sendingSms, setSendingSms] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data: patient } = useQuery({
    queryKey: ['eforms-patient', patientId],
    queryFn: () => getPatient(patientId),
    enabled: open,
    staleTime: 60_000,
  });

  const { data: surveysPage } = useQuery({
    queryKey: ['eforms-surveys'],
    queryFn: () => listSurveys({ isActive: true, perPage: 500 }),
    enabled: open,
    staleTime: 60_000,
  });

  const survey = surveysPage?.data?.find((s) => s.id === surveyId);
  const formTitle = survey?.title ?? 'General Intake Form';

  useEffect(() => {
    if (!open) {
      setLinkData(null);
      setLinkError(null);
      setFeedback(null);
      setCopied(false);
      return;
    }
    if (surveyId <= 0) return;

    setLinkLoading(true);
    setLinkError(null);
    getEFormLink(patientId, { surveyId, planId: null, customCodes: {} })
      .then(setLinkData)
      .catch((err) => {
        setLinkError(getErrorMessage(err, 'Failed to generate link'));
        setLinkData(null);
      })
      .finally(() => setLinkLoading(false));
  }, [open, patientId, surveyId]);

  const hasLink = !!linkData;
  const hasEmail = !!patient?.email;
  const hasCell = !!patient?.cellPhone;
  // Transactional opt-out is enforced server-side (422 EMAIL_OPTED_OUT /
  // SMS_OPTED_OUT); mirrored here so the button is dead before the round-trip.
  const emailOptedOut = isEmailOptedOut(patient);
  const smsOptedOut = isSmsOptedOut(patient);

  const optOutNotice =
    emailOptedOut && smsOptedOut
      ? 'This patient has opted out of transactional emails and SMS messages. Use the short link above instead.'
      : emailOptedOut
        ? 'This patient has opted out of transactional emails.'
        : smsOptedOut
          ? 'This patient has opted out of transactional SMS messages.'
          : null;

  function handleDisplay() {
    if (!linkData) return;
    window.open(linkData.fullUrl, '_blank', 'noopener,noreferrer');
  }

  function handleCopy() {
    if (!linkData) return;
    void navigator.clipboard.writeText(linkData.shortUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  async function handleSendEmail() {
    if (!linkData || !patient?.email) return;
    if (isEmailOptedOut(patient)) {
      setFeedback('This patient has opted out of transactional emails.');
      return;
    }
    if (!window.confirm('E-mail this form to the patient?')) return;

    setSendingEmail(true);
    setFeedback(null);
    try {
      let subject: string;
      let body: string;
      const emailTemplateId = survey?.emailContentId;

      if (emailTemplateId && emailTemplateId !== 0) {
        const template = await getEmailTemplate(emailTemplateId);
        subject = template.subject.replace(/\$\{FormUrl\}/g, linkData.shortUrl);
        body = template.body.replace(
          /\$\{FormUrl\}/g,
          `<a href="${linkData.shortUrl}">${linkData.shortUrl}</a>`,
        );
        [subject, body] = await Promise.all([
          replaceKeywords(patientId, subject),
          replaceKeywords(patientId, body),
        ]);
      } else {
        subject = 'Electronic Form';
        body = `Please complete the following form:<br><a href="${linkData.shortUrl}">${linkData.shortUrl}</a>`;
      }

      await sendEmail({
        patientId,
        planId: null,
        toEmail: patient.email,
        toName: `${patient.firstName ?? ''} ${patient.lastName ?? ''}`.trim() || null,
        subject,
        htmlContent: body,
      });
      setFeedback('E-form e-mailed.');
    } catch (err) {
      // getErrorMessage, not err.message: an AxiosError *is* an Error, so
      // err.message is "Request failed with status code 422" instead of the
      // server's reason (e.g. the transactional opt-out rejection).
      setFeedback(getErrorMessage(err, 'Failed to send e-mail.'));
    } finally {
      setSendingEmail(false);
    }
  }

  async function handleSendSms() {
    if (!linkData || !patient?.cellPhone) return;
    if (isSmsOptedOut(patient)) {
      setFeedback('This patient has opted out of transactional SMS messages.');
      return;
    }
    if (!window.confirm('SMS this form to the patient?')) return;

    setSendingSms(true);
    setFeedback(null);
    try {
      let message: string;
      const smsTemplateId = survey?.smsId;

      if (smsTemplateId && smsTemplateId !== 0) {
        const template = await getSmsTemplate(smsTemplateId);
        message = template.message.replace(/\$\{FormUrl\}/g, linkData.shortUrl);
        message = await replaceKeywords(patientId, message);
      } else {
        message = `Please complete the following form:\n${linkData.shortUrl}`;
      }

      await sendSMS({
        patientId,
        planId: null,
        toPhone: patient.cellPhone,
        message,
        // Counts toward the automatic GI text's daily cap (never blocks this send).
        purpose: 'gi_form',
      });
      setFeedback('E-form SMS sent.');
    } catch (err) {
      setFeedback(getErrorMessage(err, 'Failed to send SMS.'));
    } finally {
      setSendingSms(false);
    }
  }

  const isSuccessFeedback = !!feedback && /sent|mailed/i.test(feedback);

  return (
    <Modal isOpen={open} onClose={onClose} title="Send General Intake Form" size="sm">
      <div className="space-y-5">
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
          <p className="text-xs font-medium text-slate-500">Form</p>
          <p className="text-sm font-semibold text-slate-900">{formTitle}</p>
        </div>

        {linkLoading && (
          <div className="flex justify-center py-4"><Spinner /></div>
        )}

        {linkError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {linkError}
          </div>
        )}

        {hasLink && (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Short link</label>
              <div className="mt-1 flex gap-2">
                <input
                  readOnly
                  value={linkData?.shortUrl ?? ''}
                  className="flex-1 min-w-0 truncate rounded border border-slate-300 bg-slate-50 px-2 py-1.5 text-xs text-slate-700"
                  onFocus={(e) => e.currentTarget.select()}
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCopy}
                  className="flex items-center gap-1 px-2"
                >
                  {copied ? (
                    <><Check className="h-3.5 w-3.5" /><span className="text-xs">Copied</span></>
                  ) : (
                    <><Copy className="h-3.5 w-3.5" /><span className="text-xs">Copy</span></>
                  )}
                </Button>
              </div>
            </div>

            {optOutNotice && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {optOutNotice}
              </div>
            )}

            <div className="grid grid-cols-1 gap-2">
              <Button
                type="button"
                variant="secondary"
                className="flex w-full items-center justify-center text-xs"
                onClick={handleSendEmail}
                disabled={!hasEmail || emailOptedOut || sendingEmail}
                title={
                  !hasEmail
                    ? 'No email on file'
                    : emailOptedOut
                      ? 'This patient has opted out of transactional emails.'
                      : undefined
                }
              >
                <Mail className="mr-2 h-4 w-4 flex-shrink-0" />
                <span className="truncate">
                  {sendingEmail ? 'Sending...' : hasEmail ? `Email ${patient?.email}` : 'No email on file'}
                </span>
              </Button>

              <Button
                type="button"
                variant="secondary"
                className="flex w-full items-center justify-center text-xs"
                onClick={handleSendSms}
                disabled={!hasCell || smsOptedOut || sendingSms}
                title={
                  !hasCell
                    ? 'No cell phone on file'
                    : smsOptedOut
                      ? 'This patient has opted out of transactional SMS messages.'
                      : undefined
                }
              >
                <MessageSquare className="mr-2 h-4 w-4 flex-shrink-0" />
                <span className="truncate">
                  {sendingSms ? 'Sending...' : hasCell ? `SMS ${formatPhone(patient?.cellPhone)}` : 'No cell phone on file'}
                </span>
              </Button>

              <Button
                type="button"
                variant="primary"
                className="flex w-full items-center justify-center text-xs"
                onClick={handleDisplay}
              >
                <ExternalLink className="mr-2 h-4 w-4 flex-shrink-0" />
                Display Form
              </Button>
            </div>
          </div>
        )}

        {feedback && (
          <div
            className={
              isSuccessFeedback
                ? 'rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700'
                : 'rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700'
            }
          >
            {feedback}
          </div>
        )}
      </div>
    </Modal>
  );
}
